import { ConversationPartModel } from 'store/intercomSlice';
import { TriageModel } from 'data/api/triage.api';
import {
  ConversationStatusEvent,
  InboxStatus,
} from 'data/api/conversation.api';
import { recipientNames, triageCreatedAt } from 'utils/triage';

// The thread renders an ordered, heterogeneous list: Intercom messages, the
// triages raised for the same family, and its open/snoozed/closed changes, in
// the order they happened.
export type ThreadItem =
  | { kind: 'message'; id: string; at?: number; part: ConversationPartModel }
  | {
      kind: 'event';
      id: string;
      at?: number;
      label: string;
      detail?: string;
    }
  | {
      kind: 'status';
      id: string;
      at?: number;
      status: InboxStatus;
      label: string;
      // Who did it; absent when the snooze timer did.
      actor?: string;
      snoozedUntil?: string | null;
    };

export const messagesToThreadItems = (
  parts?: ConversationPartModel[],
): ThreadItem[] =>
  (parts || []).map((part) => ({
    kind: 'message',
    id: part.conversationPartId,
    at: part.createdAt,
    part,
  }));

export const triagesToThreadItems = (triages?: TriageModel[]): ThreadItem[] =>
  (triages || []).map((triage) => {
    const to = recipientNames(triage, 'to');
    const type = triage.triageOptionsType || '';
    const subject = triage.subject || '';
    return {
      kind: 'event',
      id: triage.triagesId,
      // Seconds, not milliseconds: Intercom parts carry a Unix-seconds
      // createdAt (the renderer reads them with moment.unix), so a triage
      // timed in ms would sort after every message that ever existed and the
      // inline events would all pile up at the bottom of the thread.
      at: Math.floor(triageCreatedAt(triage) / 1000),
      label: to.length ? `Triaged to ${to.join(', ')}` : 'Triaged',
      detail: [type, subject].filter(Boolean).join(' · ') || undefined,
    };
  });

const ACTOR_FALLBACK: Record<ConversationStatusEvent['actorType'], string> = {
  staff: 'a care team member',
  contact: 'a care contact',
  member: 'the family',
  system: '',
};

const statusLabel = (event: ConversationStatusEvent): string => {
  if (event.toStatus === 'snoozed') return 'Snoozed';
  if (event.toStatus === 'closed') return 'Closed';
  return event.actorType === 'system' ? 'Snooze ended' : 'Reopened';
};

export const statusEventsToThreadItems = (
  events?: ConversationStatusEvent[],
): ThreadItem[] =>
  (events || []).map((event) => ({
    kind: 'status',
    id: event.conversationStatusEventId,
    // Seconds, like every other item here — createdDate is ISO.
    at: Math.floor(new Date(event.createdDate).valueOf() / 1000),
    status: event.toStatus,
    label: statusLabel(event),
    actor:
      event.actorType === 'system'
        ? undefined
        : event.actorName || ACTOR_FALLBACK[event.actorType],
    snoozedUntil: event.snoozedUntil,
  }));

/**
 * One thread in time order. Items with no timestamp keep their position at
 * the front rather than being dropped — an undated message is still part of
 * the record. The sort is stable, so items in the same second keep their
 * list order: a close-reopen-close in one second still reads in sequence.
 */
export const mergeThreadItems = (...lists: ThreadItem[][]): ThreadItem[] =>
  ([] as ThreadItem[])
    .concat(...lists)
    .sort((a, b) => (a.at || 0) - (b.at || 0));
